import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, Globe, Package, Building2, CheckCircle2, TrendingUp, Lightbulb, Shield, FileText, Download, IndianRupee, HeartHandshake, Star, Send } from 'lucide-react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { categories as mockCategories, companyInfo, clients as mockClients, newArrivals as mockNewArrivals } from '../mock';
import AnimatedCategoryCard from '../components/AnimatedCategoryCard';
import { getCategories, getClients, getProducts, getUniversalCatalog, getReviews, submitReview } from '../services/api';

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const [categories, setCategories] = useState(mockCategories);
  const [clients, setClients] = useState(mockClients);
  const [newArrivals, setNewArrivals] = useState(mockNewArrivals);
  const [catalogUrl, setCatalogUrl] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ customer_name: '', company: '', designation: '', review_text: '', rating: 5 });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  useEffect(() => {
    const fetchLiveData = async () => {
      try {
        const [catRes, clientRes, newRes, catUrlRes, reviewRes] = await Promise.all([
          getCategories().catch(() => null),
          getClients().catch(() => null),
          getProducts({ is_new_arrival: true, limit: 4 }).catch(() => null),
          getUniversalCatalog().catch(() => null),
          getReviews().catch(() => null)
        ]);
        if (catRes?.data?.length > 0) setCategories(catRes.data);
        if (clientRes?.data?.length > 0) {
          setClients(clientRes.data.map(c => ({
            ...c,
            initials: c.name?.substring(0, 2).toUpperCase(),
            color: c.color || '#2E3A6F'
          })));
        }
        if (newRes?.data?.products?.length > 0) {
          setNewArrivals(newRes.data.products.map(p => ({
            id: p.id,
            name: p.name,
            category: p.category_id,
            image: p.images?.[0]?.url ? `${process.env.REACT_APP_BACKEND_URL}${p.images[0].url}` : `https://placehold.co/600x400/e5e7eb/6b7280?text=${encodeURIComponent('No image')}`,
            badge: 'NEW',
            description: p.short_description || p.description,
            catalog_pdf: p.catalog_pdf
          })));
        }
        if (catUrlRes?.data?.catalog_url) setCatalogUrl(catUrlRes.data.catalog_url);
        if (reviewRes?.data?.length > 0) setReviews(reviewRes.data);
      } catch { /* fall back to mock data */ }
    };
    fetchLiveData();
  }, []);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setReviewSubmitting(true);
    try {
      await submitReview(reviewForm);
      setShowReviewForm(false);
      setReviewForm({ customer_name: '', company: '', designation: '', review_text: '', rating: 5 });
      alert('Thank you! Your review has been submitted and will appear after approval.');
    } catch { alert('Failed to submit review. Please try again.'); }
    finally { setReviewSubmitting(false); }
  };

  const stats = [
    { icon: Award, label: 'Years Experience', value: companyInfo.experience },
    { icon: Users, label: 'Happy Clients', value: companyInfo.clients },
    { icon: Package, label: 'Products', value: companyInfo.products },
    { icon: Users, label: 'Skilled Professionals', value: companyInfo.professionals }
  ];

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  // Floating animation for product images
  const floatingAnimation = {
    y: [0, -20, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section - Original Clean Design */}
      <section className="relative bg-gradient-to-br from-[#f8f9fb] via-white to-[#f1f3f8] py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <motion.div 
            className="absolute top-20 left-10 w-72 h-72 bg-[#2E3A6F] rounded-full filter blur-3xl"
            animate={{ 
              scale: [1, 1.2, 1],
              x: [0, 50, 0],
              y: [0, 30, 0]
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          ></motion.div>
          <motion.div 
            className="absolute bottom-20 right-10 w-96 h-96 bg-[#4a5f9f] rounded-full filter blur-3xl"
            animate={{ 
              scale: [1, 1.3, 1],
              x: [0, -50, 0],
              y: [0, -30, 0]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          ></motion.div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="max-w-4xl mx-auto text-center space-y-8"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div className="inline-block" variants={fadeInUp}>
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#2E3A6F]/10 text-[#2E3A6F] text-sm font-medium">
                Precision Tooling Solutions
              </span>
            </motion.div>
            
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight"
              variants={fadeInUp}
            >
              We Listen,<br />
              <span className="text-[#2E3A6F]">We Innovate</span>
            </motion.h1>
            
            <motion.p 
              className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed"
              variants={fadeInUp}
            >
              Leading manufacturer and supplier of precision cutting tools and tooling solutions that enhance productivity and manufacturing excellence.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              variants={fadeInUp}
            >
              <Link to="/products">
                <motion.div
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(46, 58, 111, 0.3)" }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button size="lg" className="bg-[#2E3A6F] hover:bg-[#1f2747] group">
                    Explore Products
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                  </Button>
                </motion.div>
              </Link>
              <Link to="/enquiry">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button size="lg" variant="outline" className="border-2 border-[#2E3A6F] text-[#2E3A6F] hover:bg-[#2E3A6F] hover:text-white">
                    Get Quote
                  </Button>
                </motion.div>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {stats.map((stat, index) => (
              <motion.div 
                key={index} 
                className="text-center space-y-3 group"
                variants={scaleIn}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#2E3A6F]/10 group-hover:bg-[#2E3A6F] transition-colors duration-300">
                  <stat.icon className="text-[#2E3A6F] group-hover:text-white transition-colors" size={28} />
                </div>
                <div>
                  <motion.p 
                    className="text-3xl font-bold text-gray-900"
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    {stat.value}
                  </motion.p>
                  <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-20 bg-gradient-to-b from-white to-[#f8f9fb]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16 space-y-4"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Our Product Range</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive tooling solutions for all your machining needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <AnimatedCategoryCard key={category.id} category={category} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16 space-y-4"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Why Choose</h2>
            <div className="flex items-center justify-center gap-3">
              <img src="/favicon.png" alt="Namoh Toolings" className="h-12 w-12 object-contain" />
              <span className="text-2xl sm:text-3xl font-bold text-[#2E3A6F]">Namoh Toolings</span>
            </div>
            <p className="text-base text-[#2E3A6F] font-medium tracking-widest uppercase">We Listen We Innovate</p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {[
              {
                icon: Lightbulb,
                title: 'Innovative & Dedicated Engineers',
                description: 'Our innovative and dedicated engineers embody our tagline: "WE LISTEN WE INNOVATE"',
                color: 'from-[#2E3A6F] to-[#1a2350]'
              },
              {
                icon: IndianRupee,
                title: 'We Value Your Money',
                description: 'We take pride in our ability to optimize cost per component and save valuable time for our customers.',
                color: 'from-[#2E3A6F] to-[#1a2350]'
              },
              {
                icon: HeartHandshake,
                title: 'We Value Your Concerns',
                description: 'We go beyond merely providing tools. We are committed to supporting our customers with comprehensive machining programming solutions.',
                color: 'from-[#2E3A6F] to-[#1a2350]'
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -10, scale: 1.03 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="border-0 bg-gradient-to-br from-[#f8f9fb] to-white p-6 hover:shadow-2xl transition-all duration-500 group h-full relative overflow-hidden" data-testid={`why-choose-card-${index}`}>
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#2E3A6F] to-[#4a5a9f] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                  <CardContent className="p-0 space-y-5 text-center">
                    <motion.div 
                      className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${value.color} flex items-center justify-center shadow-lg`}
                      whileHover={{ rotate: 15, scale: 1.15 }}
                      transition={{ duration: 0.4, type: "spring" }}
                    >
                      <value.icon className="text-white" size={36} />
                    </motion.div>
                    <h3 className="text-lg font-bold text-gray-900">{value.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-20 bg-gradient-to-b from-[#f8f9fb] to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16 space-y-4"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">What Our Clients Say</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Trusted by industry leaders across India
            </p>
          </motion.div>

          {reviews.length > 0 ? (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {reviews.slice(0, 6).map((review, index) => (
                <motion.div
                  key={review.id}
                  variants={scaleIn}
                  whileHover={{ y: -10, scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="border-0 bg-white hover:shadow-xl transition-all duration-300 h-full" data-testid={`review-card-${index}`}>
                    <CardContent className="p-6 space-y-4">
                      <motion.div 
                        className="flex text-yellow-500"
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        {[...Array(review.rating || 5)].map((_, i) => (
                          <Star key={i} size={16} className="fill-yellow-500 text-yellow-500" />
                        ))}
                        {[...Array(5 - (review.rating || 5))].map((_, i) => (
                          <Star key={`e-${i}`} size={16} className="text-gray-200" />
                        ))}
                      </motion.div>
                      <p className="text-gray-700 italic text-sm leading-relaxed">"{review.review_text}"</p>
                      <div className="pt-4 border-t">
                        <p className="font-semibold text-gray-900 text-sm">{review.customer_name}</p>
                        <p className="text-xs text-gray-600">{review.designation && `${review.designation}, `}{review.company}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="text-center text-gray-500 py-12">
              <p>Be the first to share your experience with Namoh Toolings!</p>
            </div>
          )}

          {/* Submit Review Button */}
          <div className="text-center mt-10">
            {!showReviewForm ? (
              <Button
                variant="outline"
                className="border-[#2E3A6F] text-[#2E3A6F] hover:bg-[#2E3A6F] hover:text-white"
                onClick={() => setShowReviewForm(true)}
                data-testid="write-review-btn"
              >
                <Send size={16} className="mr-2" /> Write a Review
              </Button>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-lg mx-auto"
              >
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6 space-y-4">
                    <h3 className="text-lg font-bold text-gray-900 text-left">Share Your Experience</h3>
                    <form onSubmit={handleReviewSubmit} className="space-y-3 text-left">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-700">Name *</label>
                          <input
                            className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#2E3A6F] focus:border-transparent outline-none"
                            required value={reviewForm.customer_name}
                            onChange={(e) => setReviewForm({ ...reviewForm, customer_name: e.target.value })}
                            placeholder="Your name"
                            data-testid="review-form-name"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-700">Company</label>
                          <input
                            className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#2E3A6F] focus:border-transparent outline-none"
                            value={reviewForm.company}
                            onChange={(e) => setReviewForm({ ...reviewForm, company: e.target.value })}
                            placeholder="Company name"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700">Designation</label>
                        <input
                          className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#2E3A6F] focus:border-transparent outline-none"
                          value={reviewForm.designation}
                          onChange={(e) => setReviewForm({ ...reviewForm, designation: e.target.value })}
                          placeholder="e.g. Plant Manager"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700">Rating</label>
                        <div className="flex gap-1 mt-1">
                          {[1, 2, 3, 4, 5].map(i => (
                            <button key={i} type="button" onClick={() => setReviewForm({ ...reviewForm, rating: i })}>
                              <Star size={24} className={`cursor-pointer hover:scale-110 transition-transform ${i <= reviewForm.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700">Your Review *</label>
                        <textarea
                          className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#2E3A6F] focus:border-transparent outline-none resize-none"
                          rows={3} required value={reviewForm.review_text}
                          onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                          placeholder="Share your experience..."
                          data-testid="review-form-text"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button type="submit" className="flex-1 bg-[#2E3A6F]" disabled={reviewSubmitting} data-testid="review-form-submit">
                          {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowReviewForm(false)}>Cancel</Button>
                      </div>
                      <p className="text-[10px] text-gray-400 text-center">Your review will be visible after admin approval</p>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* New Arrivals Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#f8f9fb]" data-testid="new-arrivals-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16 space-y-4"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-flex items-center px-4 py-2 rounded-full bg-red-50 text-red-600 text-sm font-semibold tracking-wide"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              CATCH UP WHAT'S NEW
            </motion.span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">New Arrivals at<br /><span className="text-[#2E3A6F]">Namoh Toolings</span></h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Discover our latest additions engineered for maximum performance
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {newArrivals.map((item, index) => (
              <motion.div
                key={item.id}
                variants={fadeInUp}
                whileHover={{ y: -8 }}
                transition={{ duration: 0.3 }}
                data-testid={`new-arrival-card-${index}`}
              >
                <Card className="border-0 overflow-hidden group hover:shadow-2xl transition-all duration-500 h-full bg-white">
                  <div className="relative overflow-hidden">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full shadow-lg">
                        {item.badge}
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <CardContent className="p-5 space-y-2">
                    <span className="text-xs font-medium text-[#2E3A6F] uppercase tracking-wider">{item.category}</span>
                    <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2">{item.name}</h3>
                    <p className="text-gray-600 text-xs leading-relaxed line-clamp-2">{item.description}</p>
                    {item.catalog_pdf ? (
                      <a 
                        href={item.catalog_pdf.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${item.catalog_pdf}` : item.catalog_pdf} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-[#2E3A6F] text-xs font-semibold mt-2 group-hover:gap-2 transition-all"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`new-arrival-catalog-${index}`}
                      >
                        View Catalog <ArrowRight size={14} className="ml-1" />
                      </a>
                    ) : (
                      <Link to="/products" className="inline-flex items-center text-[#2E3A6F] text-xs font-semibold mt-2 group-hover:gap-2 transition-all">
                        View Details <ArrowRight size={14} className="ml-1" />
                      </Link>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Clientele Section */}
      <section className="py-20 bg-white border-y" data-testid="clientele-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16 space-y-4"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#2E3A6F]/10 mb-4">
              <Building2 className="text-[#2E3A6F]" size={32} />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Our Esteemed Clientele</h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Trusted by leading companies across diverse industries
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {clients.map((client, index) => (
              <motion.div
                key={client.id}
                variants={scaleIn}
                whileHover={{ 
                  scale: 1.05, 
                  y: -5,
                  boxShadow: "0 10px 30px rgba(46, 58, 111, 0.2)"
                }}
                transition={{ duration: 0.3 }}
                data-testid={`client-card-${index}`}
              >
                {client.website_url ? (
                  <a href={client.website_url} target="_blank" rel="noopener noreferrer" className="block h-full">
                    <Card className="border-2 border-gray-100 hover:border-[#2E3A6F]/30 transition-all duration-300 h-full">
                      <CardContent className="p-4 md:p-6 text-center space-y-3 flex flex-col items-center justify-center min-h-[140px] sm:min-h-[160px] md:min-h-[180px]">
                        {client.logo ? (
                          <img
                            src={client.logo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${client.logo}` : client.logo}
                            alt={client.name}
                            className="h-14 sm:h-16 max-w-[120px] object-contain"
                          />
                        ) : (
                          <motion.div 
                            className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-md"
                            style={{ backgroundColor: client.color || '#2E3A6F' }}
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            transition={{ duration: 0.3 }}
                          >
                            {client.initials || client.name?.substring(0, 2).toUpperCase()}
                          </motion.div>
                        )}
                        <div>
                          <h3 className="font-bold text-gray-900 text-xs sm:text-sm">{client.name}</h3>
                          <p className="text-xs text-gray-600 mt-1 hidden sm:block">{client.industry}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </a>
                ) : (
                  <Card className="border-2 border-gray-100 hover:border-[#2E3A6F]/30 transition-all duration-300 h-full">
                    <CardContent className="p-4 md:p-6 text-center space-y-3 flex flex-col items-center justify-center min-h-[140px] sm:min-h-[160px] md:min-h-[180px]">
                      {client.logo ? (
                        <img
                          src={client.logo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${client.logo}` : client.logo}
                          alt={client.name}
                          className="h-14 sm:h-16 max-w-[120px] object-contain"
                        />
                      ) : (
                        <motion.div 
                          className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-md"
                          style={{ backgroundColor: client.color || '#2E3A6F' }}
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          transition={{ duration: 0.3 }}
                        >
                          {client.initials || client.name?.substring(0, 2).toUpperCase()}
                        </motion.div>
                      )}
                      <div>
                        <h3 className="font-bold text-gray-900 text-xs sm:text-sm">{client.name}</h3>
                        <p className="text-xs text-gray-600 mt-1 hidden sm:block">{client.industry}</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <p className="text-gray-600 text-base md:text-lg">
              <span className="font-bold text-[#2E3A6F]">500+</span> satisfied customers with 
              <span className="font-bold text-[#2E3A6F]"> 20,000+</span> quality products
            </p>
          </motion.div>
        </div>
      </section>

      {/* Universal Catalog Download Section */}
      <section className="py-16 bg-gradient-to-br from-[#2E3A6F] to-[#1f2747] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <motion.div 
            className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl"
            animate={{ scale: [1, 1.2, 1], x: [0, 50, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl"
            animate={{ scale: [1, 1.3, 1], x: [0, -50, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 md:p-12 border border-white/20">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <motion.div 
                  className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  <FileText size={48} className="text-white" />
                </motion.div>
                
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4">Complete Product Catalog</h2>
                  <p className="text-lg text-white/90 mb-6">
                    Download our comprehensive catalog featuring all products with specifications, technical details, and pricing information.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {catalogUrl ? (
                        <a href={catalogUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${catalogUrl}` : catalogUrl} target="_blank" rel="noopener noreferrer">
                          <Button 
                            size="lg"
                            className="bg-white text-[#2E3A6F] hover:bg-gray-100 transition-all duration-300 hover:shadow-xl group"
                          >
                            <Download className="mr-2 group-hover:translate-y-1 transition-transform" size={20} />
                            Download Complete Catalog
                          </Button>
                        </a>
                      ) : (
                        <Button 
                          size="lg"
                          className="bg-white/50 text-[#2E3A6F] cursor-not-allowed"
                          disabled
                        >
                          <Download className="mr-2" size={20} />
                          Catalog Coming Soon
                        </Button>
                      )}
                    </motion.div>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Link to="/products">
                        <Button 
                          size="lg"
                          variant="outline"
                          className="border-2 border-white text-white hover:bg-white hover:text-[#2E3A6F] transition-all duration-300"
                        >
                          Browse Products Online
                        </Button>
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2E3A6F] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <motion.div 
            className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl"
            animate={{ 
              scale: [1, 1.5, 1],
              x: [0, 50, 0],
              y: [0, 50, 0]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          ></motion.div>
          <motion.div 
            className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl"
            animate={{ 
              scale: [1, 1.3, 1],
              x: [0, -50, 0],
              y: [0, -50, 0]
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          ></motion.div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="max-w-3xl mx-auto text-center space-y-8"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.h2 
              className="text-3xl sm:text-4xl font-bold"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Ready to Get Started?
            </motion.h2>
            <motion.p 
              className="text-lg text-white/90"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Contact us today for a consultation or to request a quote for your tooling needs.
            </motion.p>
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Link to="/enquiry">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button size="lg" className="bg-white text-[#2E3A6F] hover:bg-gray-100 transition-all duration-300 hover:shadow-xl">
                    Send Enquiry
                  </Button>
                </motion.div>
              </Link>
              <Link to="/dealership">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#2E3A6F] transition-all duration-300">
                    Become a Dealer
                  </Button>
                </motion.div>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
